// FLOW Platform — accessibility helpers.
//
// Small, framework-free utilities the shell uses to keep the experience
// usable with a keyboard and with assistive technology. These exist as
// their own module so future game-loop code (Step 2) can pull from the
// same primitives without re-inventing them.

/**
 * Detect whether the user has asked for reduced motion at the OS / UA
 * level. The shell respects this by not auto-running animations and by
 * keeping focus changes instant.
 *
 * Cached at first call; we don't need live re-evaluation in Step 1.
 * (If we ever do, switch to a media-query listener.)
 */
let cachedReducedMotion = null;
export function prefersReducedMotion() {
  if (cachedReducedMotion !== null) {
    return cachedReducedMotion;
  }
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    cachedReducedMotion = false;
    return false;
  }
  cachedReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return cachedReducedMotion;
}

/**
 * Create an off-screen live region that screen readers announce when
 * we update its text content. We use `aria-live="polite"` so updates
 * don't interrupt whatever the user is currently hearing.
 *
 * Returned object exposes one method:
 *
 *   announce(message)   - replace the region's text with `message`,
 *                         which causes the screen reader to speak it.
 *
 * The region is appended to the given container so it scopes to the
 * shell and tears down when the shell does.
 */
export function createLiveRegion(container) {
  const region = document.createElement('div');
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  // Visually hidden but available to assistive technology.
  region.className = 'flow-fht-sr-only';
  container.appendChild(region);

  let lastMessage = '';

  return {
    announce(message) {
      const text = String(message || '');
      // If the message didn't change, briefly clear so screen readers
      // re-announce. (Otherwise polite live regions can swallow identical
      // updates.)
      if (text === lastMessage && text !== '') {
        region.textContent = '';
        // Re-set on the next tick so the DOM mutation registers.
        setTimeout(() => { region.textContent = text; }, 50);
      } else {
        region.textContent = text;
      }
      lastMessage = text;
    },
  };
}

/**
 * Move keyboard focus to an element, accounting for reduced-motion
 * preferences and elements that aren't natively focusable.
 *
 * If the element isn't focusable (no tabindex, no inherent focusability),
 * we add `tabindex="-1"` so it can be focused programmatically without
 * entering the tab order.
 */
export function focusElement(el) {
  if (!el) return;
  if (!el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1');
  }
  try {
    el.focus({ preventScroll: prefersReducedMotion() });
  } catch (e) {
    // Old browsers without options support.
    el.focus();
  }
}
